import type { ReactElement } from 'react';
import type { GetStaticProps, GetStaticPropsContext, GetStaticPaths, GetStaticPathsContext } from 'next';
import type {
  CategoryRequestResultProps,
  CategoryViewProps,
  GameRequestProps,
  CategoryProps,
  CategoryRequestProps,
} from '../../types';

import * as React from 'react';

import { View } from '../../generated/category';
import { BaseLayout } from '../../layouts/BaseLayout';

import { NextSeo, CollectionPageJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAppContext } from '../../contexts/AppContext';
import { getLocalizedPaths } from '../../utils/localize-helpers';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getAllCollections, getCollection, getLatestGames } from '../../app/api';
import { mappingCollection, mappingGamesMinimal } from '../../app/mappings';
import { CollectionTypesEnum } from '../../types/enum';
import { translateUrl } from 'next-translate-routes';
import { getNonPageNamespaces } from '../../utils/get-non-page-namespaces';

export default function DynamicPage(props: CategoryViewProps) {
  const { title, collection } = props;
  const { t, i18n } = useTranslation(['common', 'category']);
  const appContext = useAppContext();
  const { name, assetAbsoluteUrl } = appContext.useConfig();
  const router = useRouter();
  const paths = getLocalizedPaths(router);
  const pageContextWithPaths = { ...props.pageContext, paths };
  let seoTitle = i18n.exists('category:title', { fallbackLng: props.pageContext.locale })
    ? t('category:title', { title })
    : t('common:title', { title });
  let seoDescription = i18n.exists('category:description', { fallbackLng: props.pageContext.locale })
    ? t('category:description', { title, description: collection.description })
    : t('common:description', { title, description: collection.description });
  let seoContent = '';
  let seoFAQSchema = ';';
  // Customized SEO
  const { seo } = appContext.useSeo();

  // Categories page
  if (seo?.categories) {
    const currentPageSEO = seo.categories?.[router.query.slug as keyof typeof seo.categories];
    if (currentPageSEO && router.locale) {
      const seoCurrentLanguage = currentPageSEO?.[router.locale];
      if (seoCurrentLanguage?.title) {
        seoTitle = seoCurrentLanguage?.title;
      }
      if (seoCurrentLanguage?.description) {
        seoDescription = seoCurrentLanguage?.description;
      }
      if (seoCurrentLanguage?.h1_description) {
        if (seoCurrentLanguage?.show_description) {
          seoContent = seoCurrentLanguage.h1_description?.content || '';
        }
        if (seoCurrentLanguage?.h1_description?.seo) {
          seoFAQSchema = JSON.stringify(seoCurrentLanguage?.h1_description?.seo) || '';
        }
      }
    }
  }

  return (
    <>
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        openGraph={{
          images: [
            {
              url: `${assetAbsoluteUrl}${
                collection.games[0]?.images?.cover ||
                collection.games[0]?.images?.cover1x1 ||
                collection.images.cover1x1 ||
                collection.images.cover
              }/?ar[r]=1x1&ar[w]=512&fmt=.png`,
              width: 512,
              height: 512,
              alt: `${seoTitle}`,
              type: 'image/png',
            },
          ],
        }}
      />
      <CollectionPageJsonLd
        name={title || seoTitle}
        hasPart={collection.games.map((game) => ({
          about: game.description,
          author: { name },
          name: game.name,
          datePublished: game.publishedAt,
          audience: 'Internet',
          keywords: 'schema',
          thumbnailUrl: `${assetAbsoluteUrl}${game.images.cover1x1 || game.images.cover}/?ar[r]=1x1&ar[w]=512&fmt=.png`,
          image: `${assetAbsoluteUrl}${game.images.cover16x9 || game.images.cover1x1 || game.images.cover}/`,
        }))}
      />
      <BreadcrumbJsonLd
        itemListElements={pageContextWithPaths.breadcrumbs.map((crumb, index) => ({
          position: index + 1,
          name: crumb.name,
          item: crumb.href,
        }))}
      />
      <View {...props} pageContext={pageContextWithPaths} />
      <div dangerouslySetInnerHTML={{ __html: seoContent }} style={{ padding: '24px' }} />
      <script type="application/ld+json">{seoFAQSchema}</script>
    </>
  );
}

// Adding getLayout property. Allowing us to return a React component for the layout.
// This allows us to define the layout on a per-page basis. Since we're returning a function,
// we can have complex nested layouts if desired.
DynamicPage.getLayout = function getLayout(page: ReactElement) {
  const { props } = page;
  const { pageContext } = props;
  return (
    <BaseLayout pageType={CollectionTypesEnum.CATEGORY} pageContext={pageContext}>
      {page}
    </BaseLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async (context: GetStaticPathsContext) => {
  // Request all categories by locale for static pre-rendering.
  // Always make sure we return the object below.
  // https://nextjs.org/docs/advanced-features/i18n-routing#dynamic-routes-and-getstaticprops-pages
  const locales: (string | undefined)[] = context?.locales || [context.defaultLocale];
  const promises: Promise<CategoryRequestProps[] | undefined>[] = locales.map((locale) =>
    getAllCollections(
      CollectionTypesEnum.CATEGORY,
      {
        _limit: 6,
        _fieldsGame: 'image',
      },
      {
        ssr: true,
        locale,
      }
    )
  );
  const response: (CategoryRequestProps[] | undefined)[] = await Promise.all(promises);
  const localizedBatches: { id: string; slug: string; locale: string | undefined }[][] = Array.isArray(response)
    ? response.map((batch: CategoryRequestProps[] | undefined, index: number) => {
        return Array.isArray(batch)
          ? batch.map((category: CategoryRequestProps) => {
              return {
                id: category.id,
                slug: category.code,
                locale: locales[index],
              };
            })
          : [];
      })
    : [];
  const categories = Array.prototype.concat.apply([], localizedBatches);

  return {
    paths: categories.map((path: CategoryProps) => ({
      params: {
        slug: path.slug,
      },
      locale: path.locale,
    })),
    // Use server side rendering (SSR) when a page is not cached in our Next file system.
    // Let the user or search bot wait until then; before continuing the route to the designated page.
    // https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-true
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
  const url = `${process.env.PROJECT_URL?.replace(/\/+$/, '')}`;
  const slug: string = (context?.params?.slug || '') as string;
  const locale: string = (context.locale || context.defaultLocale) as string;
  const category: CategoryRequestProps | undefined = await getCollection(
    slug,
    CollectionTypesEnum.CATEGORY,
    {
      _fieldsGame: 'image',
    },
    {
      ssr: true,
      locale,
      throwOnError: true,
    }
  );
    const popular: CategoryRequestProps | undefined = await getCollection(
      `${process.env.PROJECT_CODE}-popular`,
      CollectionTypesEnum.COLLECTION,
      {
        _fieldsGame: 'image',
      },
      {
        ssr: true,
        locale,
      }
    );
  const latest: GameRequestProps[] | undefined = await getLatestGames(
    {
      period: 'week',
      _limit: 12,
      _gameRandom: true,
      _fieldsGame: 'image', // Include "image" property for each game.
    },
    {
      ssr: true,
      locale,
    }
  );

  let collections: CategoryRequestProps[] = [];
  if (process.env.PROJECT_ID === '90f2686f-b80c-45bb-99d6-13ee936730f5' && slug === 'puzzle') {
    const promises: Promise<CategoryRequestProps | undefined>[] = [
      'bubble-shooter-iol',
      'match-3-iol',
      'logic-iol',
      'mahjong-iol',
      'sudoku-iol',
      'cards-board-iol',
    ].map((collectionName) =>
      getCollection(
        collectionName,
        CollectionTypesEnum.COLLECTION,
        {
          _gameLimit: 12,
          _fieldsGame: 'image',
        },
        {
          ssr: true,
          locale,
        }
      )
    );
    collections = (await Promise.all(promises)) as CategoryRequestProps[];
  }

  if (!category?.id) {
    return {
      notFound: true,
      revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
    };
  }

  const page = {
    title: category?.name || null,
    body: category?.description || null,
    locale: context.locale,
  };
  const { title, body } = page;
  const pageContext = {
    locale: page.locale,
    locales: context.locales,
    defaultLocale: context.defaultLocale,
    // Todo: HUZ2ERA-977 - have the backend return translated slugs as part of the game object response.
    slugs:
      context?.locales?.map((locale) => ({
        locale,
        slug,
      })) || [],
    slug,
    breadcrumbs: [
      { href: url, path: '/', name: 'Home' },
      {
        href: `${url}${translateUrl('/categories', locale)}`,
        path: `${translateUrl('/categories', locale)}`,
        name: 'Categories', // Todo: HUZ2ERA-1112 - Translate crumb name on server side?
      },
      {
        href: `${url}${translateUrl(`/category/${slug}`, locale)}`,
        path: `${translateUrl(`/category/${slug}`, locale)}`,
        name: category?.name || '',
      },
    ],
  };
  // const localizedPaths = getLocalizedPaths(pageContext);
  const global = {
    popular: popular && Array.isArray(popular?.games) ? [...mappingGamesMinimal(popular.games)] : [],
    latest: Array.isArray(latest) ? [...mappingGamesMinimal(latest)] : [],
    collections: collections.map((collection: CategoryRequestResultProps) => ({
      ...mappingCollection(collection),
    })),
  };

  return {
    props: {
      title,
      body,
      collection: {
        ...mappingCollection(category),
      },
      global,
      pageContext,
      // Get our translations by namespace and locale.
      ...(await serverSideTranslations(locale as string, ['common', 'category', ...getNonPageNamespaces(locale)])),
    },
    // Incremental Static Regeneration (ISR).
    // https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration
    revalidate: parseInt(process.env.PROJECT_SSR_REVALIDATE_SECONDS || '') || 60,
  };
};
